import { Component } from '@angular/core';
import { AuthRoles, ChargeInformationDto, ChargeInformationService, ChargeSetupDto } from '@root/app/api/dmc';
import { TableConfig } from '@shared/interfaces/table/table-config';
import {
  chargeinformationChargesetupDifferentiatedTable,
  chargeinformationChargesetupSinglePriceTable
} from '@shared/table-configs/chargeinformation/chargeinformation-chargesetup.table';
import {
  chargeinformationChargesetupDifferentiatedDetails,
  chargeinformationChargesetupSinglePriceDetails
} from '@shared/interfaces/dmc/masterdata/chargeinformation-chargesetup-details';
import { TranslatePipe } from '@ngx-translate/core';
import { ModalService } from '@app/core/services/modal.service';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CardComponent } from '@shared/components/card/card.component';
import { TableComponent } from '@shared/components/table/table.component';
import { ValueDetailsComponent } from '@shared/components/value-details/value-details.component';
import { HeaderService } from '@app/core/services/header.service';
import {
  chargeinformationSetupPricesActionMenu
} from '@views/chargeInformation/chargeinformation-setup-prices/chargeinformation-setup-prices.action-menu';
import {
  ChargeinformationChargesetupOpenformComponent
} from '@shared/modals/chargeinformation/chargeinformation-chargesetup-openform/chargeinformation-chargesetup-openform.component';
import {
  ChargeinformationChargesetupRemoveComponent
} from '@shared/modals/chargeinformation/chargeinformation-chargesetup-remove/chargeinformation-chargesetup-remove.component';
import { Columns } from '@shared/interfaces/table/column-config';
import { hasRole } from '@app/core/guards/has-role.guard';
import { ObservationTimeSeriesPeriod } from '@app/api/dmc';

@Component({
  selector: 'app-chargeinformation-setup-prices',
  imports: [
    CardComponent,
    TableComponent,
    ValueDetailsComponent,
    TranslatePipe
  ],
  templateUrl: './chargeinformation-setup-prices.component.html',
  styleUrl: './chargeinformation-setup-prices.component.css'
})
export class ChargeinformationSetupPricesComponent {
  public id?: string;
  public chargeInformation?: ChargeInformationDto;

  public chargeSetupList: ChargeSetupDto[] = [];
  public chargeSetupTableConfig: TableConfig<ChargeSetupDto> = chargeinformationChargesetupSinglePriceTable;
  public chargeSetupData: Columns<ChargeSetupDto>[] = chargeinformationChargesetupSinglePriceDetails;

  public selectedElements: ChargeSetupDto[] = [];

  constructor(
    private readonly chargeInformationService: ChargeInformationService,
    private readonly modalService: ModalService,
    private readonly route: ActivatedRoute,
    private readonly headerMenuService: HeaderService,
  ) {
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.chargeInformationService.chargeInformationGetByIdGet(this.id).subscribe(data => {
        this.chargeInformation = data;
        this.chargeSetupTableConfig = this.usesDifferentiatedPrices
          ? chargeinformationChargesetupDifferentiatedTable
          : chargeinformationChargesetupSinglePriceTable;
        this.chargeSetupData = this.usesDifferentiatedPrices
          ? chargeinformationChargesetupDifferentiatedDetails
          : chargeinformationChargesetupSinglePriceDetails;
        if (this.chargeInformation.id) {
          this.getChargeSetupList();
        }
      });
      const ciWriter = hasRole([AuthRoles.ChargeInformationWriter]);
      this.headerMenuService.setActions(chargeinformationSetupPricesActionMenu, {
          create: () => this.openChargeSetupCreateForm(),
          edit: () => this.openChargeSetupEditForm(),
          remove: () => this.openChargeSetupRemoveModal(),
        },
        {
          create: () => !ciWriter,
          edit: () => !ciWriter || this.selectedElements.length !== 1,
          remove: () => !ciWriter || this.selectedElements.length !== 1,
        });
    })
  }

  private get usesDifferentiatedPrices(): boolean {
    return this.chargeInformation?.observationPeriod === ObservationTimeSeriesPeriod.Hour;
  }

  public getChargeSetupList() {
    this.chargeInformationService.chargeInformationGetChargeSetupGet(this.chargeInformation!.id!).subscribe(result => {
      if (result.results) {
        this.chargeSetupList = result.results;
      }
    })
  }

  public openChargeSetupCreateForm() {
    this.modalService.show({
      component: ChargeinformationChargesetupOpenformComponent,
      title: 'chargeSetup.actions.createChargeSetup',
      actions: [
        {
          type: 'primary',
          label: 'system.button.create',
          method: 'createChargeSetup'
        },
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: {
        chargeInformationId: this.chargeInformation?.id,
        chargeInformation: this.chargeInformation
      }
    }).subscribe(() => {
      this.getChargeSetupList();
    });
  }

  public openChargeSetupEditForm() {
    this.modalService.show({
      component: ChargeinformationChargesetupOpenformComponent,
      title: 'chargeSetup.actions.editChargeSetup',
      actions: [
        {
          type: 'primary',
          label: 'system.button.save',
          method: 'saveChanges'
        },
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: {
        chargeInformationId: this.chargeInformation?.id,
        chargeInformation: this.chargeInformation,
        chargeSetupDto: this.selectedElements[0] as ChargeSetupDto
      }
    }).subscribe(() => {
      this.getChargeSetupList();
    });
  }

  public openChargeSetupRemoveModal(): void {
    this.modalService.show({
      component: ChargeinformationChargesetupRemoveComponent,
      title: 'chargeSetup.actions.removeChargeSetup',
      actions: [
        {
          type: 'primary',
          label: 'system.button.confirm',
          method: 'remove'
        },
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: {
        chargeInformationId: this.chargeInformation?.id,
        chargeSetupDto: this.selectedElements[0] as ChargeSetupDto
      }
    }).subscribe(() => {
      this.getChargeSetupList();
    });
  }
}
